import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Activity, 
  Smartphone, 
  MapPin,
  Play,
  Power,
  Pause,
  RefreshCw,
  Eye,
  Search,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  FileText
} from 'lucide-react';
import type { UserAnalyticsSession, UserAnalyticsTimelineEvent } from '../../services/MasterAdmin/userService';

const formatDateTime = (dateString: string | null | undefined) => {
  if (dateString == null || dateString === '') return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const SessionDetailPage: React.FC = () => {
  const { userId, sessionId } = useParams<{ userId: string; sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { session?: UserAnalyticsSession; user?: { userId: string; name?: string } } | null;
  const session = state?.session;
  const user = state?.user;

  const handleBack = () => {
    navigate(`/users/${userId}`, { state: { user, defaultTab: 'timeline' } });
  };

  if (!userId || !sessionId) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600 dark:text-red-400">Missing user or session in URL.</p>
        <button
          onClick={() => navigate('/users')}
          className="mt-4 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
        >
          Back to Users
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-8 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Session data not available. Please open this page from the user&apos;s Sessions list.
        </p>
        <button
          onClick={handleBack}
          className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
        >
          Back to User
        </button>
      </div>
    );
  }

  const events = session.events || [];

  const getEventTargetLabel = (evt: UserAnalyticsTimelineEvent) => {
    const raw = evt.screenLabel || evt.entityId;
    if (raw == null || String(raw).trim() === '') return null;
    return String(raw).trim();
  };

  const getStepDwellTime = (currentIdx: number): string | null => {
    if (currentIdx >= events.length - 1) return null;
    const currentEvt = events[currentIdx];
    const nextEvt = events[currentIdx + 1];
    if (!currentEvt.timestamp || !nextEvt.timestamp) return null;
    
    const d1 = new Date(currentEvt.timestamp).getTime();
    const d2 = new Date(nextEvt.timestamp).getTime();
    const diffMs = d2 - d1;
    if (diffMs < 1000) return null; 
    
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `Stayed ${diffSec}s`;
    
    const diffMin = Math.floor(diffSec / 60);
    const remSec = diffSec % 60;
    return `Stayed ${diffMin}m ${remSec}s`;
  };

  // Helper to determine border styling for conversion/success or exit/failure (including hover overrides)
  const getEventBorderClass = (eventType: string): string => {
    const successTypes = new Set([
      "AMBULANCE_BOOKED",
      "MEDICINE_ORDER_CREATED",
      "PAYMENT_SUCCESS",
      "CLINIC_APPOINTMENT_CREATED",
      "BED_BOOKING_CREATED",
      "LAB_TEST_BOOKING_CREATED"
    ]);

    const exitTypes = new Set([
      "PAYMENT_FAILED",
      "APP_CLOSE",
      "APP_CRASH",
      "OTP_FAILED"
    ]);

    if (successTypes.has(eventType)) {
      return "border-l-[5px] border-emerald-400 bg-emerald-500/5 dark:bg-emerald-500/10 hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20";
    }

    if (exitTypes.has(eventType) || eventType.toLowerCase().includes("background") || eventType.toLowerCase().includes("failed")) {
      return "border-l-[5px] border-red-500 bg-red-500/5 dark:bg-red-500/10 hover:bg-red-500/10 dark:hover:bg-red-500/20";
    }

    return "border-l-[5px] border-gray-200 dark:border-gray-700/60 hover:bg-gray-50/40 dark:hover:bg-gray-700/20";
  };

  const getEventIcon = (eventType: string) => {
    const iconClass = "w-4 h-4";
    const successTypes = new Set([
      "AMBULANCE_BOOKED",
      "MEDICINE_ORDER_CREATED",
      "PAYMENT_SUCCESS",
      "CLINIC_APPOINTMENT_CREATED",
      "BED_BOOKING_CREATED",
      "LAB_TEST_BOOKING_CREATED"
    ]);

    const exitTypes = new Set([
      "PAYMENT_FAILED",
      "APP_CLOSE",
      "APP_CRASH",
      "OTP_FAILED"
    ]);

    if (successTypes.has(eventType)) {
      return <CheckCircle className={`${iconClass} text-emerald-500`} />;
    }

    if (exitTypes.has(eventType) || eventType.toLowerCase().includes("failed")) {
      return <AlertTriangle className={`${iconClass} text-red-500`} />;
    }

    if (eventType === "APP_BACKGROUND") {
      return <Pause className={`${iconClass} text-orange-400`} />;
    }

    switch (eventType) {
      case "APP_OPEN":
      case "SESSION_STARTED":
      case "USER_LOGIN":
        return <Play className={`${iconClass} text-sky-500`} />;
      case "APP_FOREGROUND":
        return <RefreshCw className={`${iconClass} text-indigo-400`} />;
      case "SEARCH_PERFORMED":
        return <Search className={`${iconClass} text-amber-500`} />;
      case "PAYMENT_INITIATED":
        return <CreditCard className={`${iconClass} text-violet-500`} />;
      case "SCREEN_VIEW":
      case "HOME_OPENED":
        return <Eye className={`${iconClass} text-slate-500 dark:text-slate-400`} />;
      default:
        return <FileText className={`${iconClass} text-slate-400`} />;
    }
  };

  return (
    <div className="w-full min-w-0 max-w-4xl mx-auto p-3 sm:p-4 md:p-6">
      <div className="pb-4 border-b border-gray-100 dark:border-gray-700 mb-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to user</span>
        </button>
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {formatDateTime(session.sessionStart)}
          {session.sessionEnd && ` – ${formatDateTime(session.sessionEnd)}`}
          {' · '}
          {session.eventsCount} event{session.eventsCount !== 1 ? 's' : ''}
        </p>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {session.trackedFrom && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700">
              {session.trackedFrom}
            </span>
          )}
          {session.device && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700">
              <Smartphone className="w-3.5 h-3.5" />
              {session.device}
            </span>
          )}
          {session.city && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700">
              <MapPin className="w-3.5 h-3.5" />
              {session.city}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/80 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary-500" />
            Navigations (in order)
          </h2>
        </div>
        {events.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No events in this session.
          </div>
        ) : (
          <ol className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {events.map((evt: UserAnalyticsTimelineEvent, idx: number) => {
              const targetLabel = getEventTargetLabel(evt);
              const dwellTime = getStepDwellTime(idx);
              const borderClass = getEventBorderClass(evt.eventType);
              const isLast = idx === events.length - 1;
              return (
              <li key={evt.eventId} className={`relative flex items-start gap-4 pl-6 pr-4 py-4 transition-all ${borderClass}`}>
                {/* Vertical connecting line */}
                {!isLast && (
                  <span 
                    className="absolute left-[39px] top-[38px] bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700/80" 
                    aria-hidden="true" 
                  />
                )}
                
                {/* Circular Icon Node */}
                <div className="relative shrink-0 flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm z-10">
                  {getEventIcon(evt.eventType)}
                </div>

                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center flex-wrap gap-2">
                    {evt.eventTypeLabel ?? evt.eventType}
                    {targetLabel && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500 font-normal mx-0.5">→</span>
                        {targetLabel === "Home" ? (
                          <span className="text-gray-500 dark:text-gray-400 font-normal">{targetLabel}</span>
                        ) : (
                          <span className="text-sky-500 dark:text-sky-400 font-semibold">{targetLabel}</span>
                        )}
                      </>
                    )}
                    {dwellTime && (
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700/50 px-2 py-0.5 rounded-full border border-gray-200/50 dark:border-gray-600/30 flex items-center gap-1 shadow-sm">
                        ⏱️ {dwellTime}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {formatDateTime(evt.timestamp)}
                    {(evt.trackedFrom || evt.device) && (
                      <>
                        {' · '}
                        {[evt.trackedFrom, evt.device].filter(Boolean).join(' · ')}
                      </>
                    )}
                  </p>
                </div>
              </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default SessionDetailPage;
